/**
 * マルチプレイヤー（マッチメイキング + フレンドマッチ）関連の定数と型定義。
 *
 * ハードコード禁止ルール（.claude/rules/no-hardcode.md）に従い、
 * タイムアウト / ポーリング間隔 / ルーム TTL / モード名 / ラベルをここに集約する。
 *
 * T13: マッチメイキング基盤
 * T14: フレンドマッチ・ルーム管理
 * T15: （この設定を参照して WebSocket 接続・対戦同期を実装する）
 */

// ---------------------------------------------------------------------------
// タイミング定数
// ---------------------------------------------------------------------------

/** マッチメイキングのクライアント側タイムアウト（ms）。Hub の timeoutSec にも渡す。 */
export const MATCHMAKING_TIMEOUT_MS = 45_000

/** ticket status ポーリング間隔（ms）。 */
export const MATCHMAKING_POLL_INTERVAL_MS = 1_000

/** ルームの TTL（秒）。Hub デフォルト 30 分 / 最大 60 分の範囲内に収める。 */
export const ROOM_TTL_SEC = 30 * 60

/**
 * フレンドマッチ（ホスト側）の待機ルームを `getRoomInfo` でポーリングする間隔。
 * ゲスト参加を検知するために使う。
 */
export const ROOM_POLL_INTERVAL_MS = 2_000

// ---------------------------------------------------------------------------
// ルームパラメータ
// ---------------------------------------------------------------------------

/** デフォルトのマッチモード。Hub 側で英数字+ハイフン+アンダースコアのみ許容。 */
export const MATCH_MODE_DEFAULT = 'casual_1v1'

/** デフォルトリージョン。Hub Matchmaking API の required フィールド。 */
export const MATCH_REGION_DEFAULT = 'ap-northeast-1'

/** ビルド識別子。version.json を将来参照してもよいが、現状は固定値で十分。 */
export const MATCH_BUILD_DEFAULT = 'v1'

/** マルチプレイヤーの最大パーティサイズ（ラッキーどうぶつナワバリは 1v1）。 */
export const MULTIPLAYER_MAX_PARTY_SIZE = 1

/** ルームの定員。ナワバリは常に 1v1 のため 2 固定。 */
export const ROOM_CAPACITY = 2

// ---------------------------------------------------------------------------
// UI ラベル
// ---------------------------------------------------------------------------

export const MULTIPLAYER_UI = {
  menuTitle: 'オンライン対戦',
  menuRandomLabel: 'ランダムマッチ',
  menuRandomDescription: '知らないプレイヤーと対戦します',
  menuFriendLabel: 'あいことばで対戦',
  menuFriendDescription: '友だちと合言葉を共有して対戦します',
  menuCloseLabel: '閉じる',

  matchmakingSearchingTitle: '対戦相手を探しています…',
  matchmakingCancel: 'キャンセル',
  matchmakingTimeoutTitle: '相手が見つかりませんでした',
  matchmakingRetry: 'もう一度さがす',
  matchmakingClose: '閉じる',
  matchmakingError: 'マッチングに失敗しました',

  friendHostTabLabel: 'あいことばを発行',
  friendGuestTabLabel: 'あいことばで参加',
  friendCreateButton: 'あいことばを作る',
  friendCreateWaiting: '相手の参加を待っています…',
  friendCopyButton: 'コピー',
  friendCopied: 'コピーしました',
  friendShareButton: '共有',
  friendCancelButton: 'キャンセル',
  friendCodePlaceholder: 'あいことば（6文字）',
  friendJoinButton: '参加する',
  friendJoinError: 'あいことばが正しくないか、ルームの定員/期限切れです',
  friendError: 'エラーが発生しました',

  lobbyTitle: '対戦ロビー',
  lobbyWaiting: '対戦の準備中…',
  lobbyOpponentMissing: '相手の接続を待っています…',
  lobbyStartButton: '対戦開始',
  lobbyStartPlaceholder: 'リアルタイム対戦は T15 で実装します',
  lobbyLeaveButton: '退出',
} as const

// ---------------------------------------------------------------------------
// ランタイム型
// ---------------------------------------------------------------------------

/** Matchmaking ticket の UI 側表現。Hub の YmgMatchmakingTicketStatus と一致。 */
export type MatchmakingStatus =
  | 'IDLE'
  | 'SEARCHING'
  | 'MATCHED'
  | 'TIMED_OUT'
  | 'CANCELLED'
  | 'ERROR'

export interface MatchmakingTicket {
  ticketId: string
  expiresAt: number
  status: MatchmakingStatus
  room: RoomInfo | null
}

export interface RoomMemberInfo {
  userId: string
  joinedAt: number
  ready: boolean
}

/** UI 層で使う Room 情報の単純化表現。 */
export interface RoomInfo {
  roomId: string
  gameId: string
  platformId: string
  mode: string
  capacity: number
  hostUserId: string
  members: RoomMemberInfo[]
  state: 'OPEN' | 'FULL' | 'CLOSED' | 'EXPIRED'
  metadata?: Record<string, unknown>
  createdAt: number
  expiresAt: number
}

export interface InviteCode {
  code: string
  /** Unix epoch ms。招待コード自体の期限（ルーム TTL 以下）。 */
  expiresAt: number
}

/** useMatchmaking hook が外部に見せる状態。 */
export interface MultiplayerMatchmakingState {
  status: MatchmakingStatus
  ticket: MatchmakingTicket | null
  room: RoomInfo | null
  error: string | null
  elapsedMs: number
  timeoutReached: boolean
}
